import {
  type Stripe,
  type StripeCardNumberElement,
  type StripeElements,
} from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Configuration from "config";
import { UserContext } from "contexts/UserContext";
import { useRequiredContext } from "contexts/utils";
import moment from "moment-timezone";
import { MutableRefObject, useRef } from "react";
import { logError } from "services/logging";
import { paymentMethodsQuery } from "services/paymentMethods";
import {
  availablePricingModelsQuery,
  generateIdempotencyKey,
  postPricingModelItemPurchaseIntent,
} from "services/pricingModel";
import { PricingModelItem } from "services/pricingModel/model";
import { trackEvent } from "services/productAnalytics";
import { api } from "services/restUtil";
import {
  AccessObject,
  PaymentStatus,
  SubscriptionPlan,
} from "services/subscriptions/model";
import assert from "utils/assert";
import { memoizeAsyncFactory } from "utils/async";
import { apiQuery } from "utils/queries";
import { getUserTimezoneId } from "utils/date";
import { useSingleFlight } from "../../utils/useSingleFlight";
import { trackPricingItemPurchase, trackPricingItemPurchaseError } from "./analytics";
import { ApiRequestError } from "services/errors";

export interface SubscriptionPlanResponse {
  subscription: SubscriptionPlan | null;
  access_object: AccessObject | null;
  payment: PaymentStatus | null;
}

export const currentSubscriptionPlanQuery = apiQuery({
  key: "/subscription/plan",
  queryFn: () => api.get<SubscriptionPlanResponse>("/subscription/plan"),
});

export function deleteSubscriptionPlan() {
  return api.delete<void, SubscriptionPlan>("/subscription/plan");
}

export const getStripeClient = memoizeAsyncFactory(async () => {
  const stripe = await loadStripe(Configuration.STRIPE_API_KEY);
  assert(stripe, "Stripe failed to load");
  return stripe;
});

export type PurchaseResult = "success" | "failed" | "failed_reload_on_close";
type RefreshPurchaseData = () => void;
type IdempotencyKeyRef = MutableRefObject<string | undefined>;

type SavedMethodPurchaseExecutorArgs = {
  pricingItem: PricingModelItem;
};
export async function executeSavedMethodPurchase(
  args: SavedMethodPurchaseExecutorArgs,
  idempotencyKeyRef: IdempotencyKeyRef,
  refresh: RefreshPurchaseData
): Promise<PurchaseResult> {
  let response;

  try {
    response = await postPricingModelItemPurchaseIntent(args.pricingItem.id, {
      idempotency_key: getIdempotencyKey(idempotencyKeyRef),
      use_saved_pm: true,
    });
  } catch (error: unknown) {
    logError("Failed to purchase pricing model with saved card due to error", {
      pricingModelItem: args.pricingItem,
      error,
    });

    if (error instanceof ApiRequestError) {
      if (error.response?.status === 400) {
        // The previous attempt may already have completed under this idempotency key.
        // Refresh immediately and avoid showing a failure modal so the UI can
        // reconcile to the settled purchase state right away.
        resetIdempotencyKey(idempotencyKeyRef);
        refresh();
        return "failed_reload_on_close";
      }

      if (error.response?.status === 402) {
        // This attempt failed definitively, so future retries need a new idempotency key.
        resetIdempotencyKey(idempotencyKeyRef);
      }
    }

    return "failed";
  }

  if (response.status !== "processing") {
    refresh();
    return "success";
  }

  try {
    const stripe = await getStripeClient();
    const actionResult = await stripe.confirmCardPayment(response.client_secret);
    if (actionResult.error) {
      resetIdempotencyKey(idempotencyKeyRef);
      return "failed";
    }

    refresh();
    return "success";
  } catch {
    // A thrown Stripe confirmation error is treated as a generic failure. Unlike 4xx
    // purchase-intent responses, it should not rotate the idempotency key.
    return "failed";
  }
}

export type CardEntryPurchaseArgs = {
  stripe: Stripe;
  cardNumber: StripeCardNumberElement;
  postal: string;
};
type CardEntryPurchaseExecutorArgs = CardEntryPurchaseArgs & {
  pricingItem: PricingModelItem;
};

export async function executeCardEntryPurchase(
  args: CardEntryPurchaseExecutorArgs,
  idempotencyKeyRef: IdempotencyKeyRef,
  refresh: RefreshPurchaseData
): Promise<PurchaseResult> {
  let response;

  try {
    response = await postPricingModelItemPurchaseIntent(args.pricingItem.id, {
      idempotency_key: getIdempotencyKey(idempotencyKeyRef),
      save_pm: args.pricingItem.purchase_student_type === "self-study",
    });
  } catch (error: unknown) {
    logError(error);

    if (error instanceof ApiRequestError) {
      if (error.response?.status === 400) {
        // New-card flows keep the failure modal visible until dismissal. Refreshing
        // here can unmount PurchasePlan before the user sees the error state, so the
        // caller reloads after modal close instead.
        resetIdempotencyKey(idempotencyKeyRef);
        return "failed_reload_on_close";
      }

      if (error.response?.status === 402) {
        // This attempt failed definitively, so future retries need a new idempotency key.
        resetIdempotencyKey(idempotencyKeyRef);
      }
    }

    return "failed";
  }

  if (response.status !== "pending" && response.status !== "processing") {
    return "failed";
  }

  try {
    const result = await args.stripe.confirmCardPayment(response.client_secret, {
      payment_method: {
        card: args.cardNumber,
        billing_details: {
          address: {
            postal_code: args.postal,
          },
        },
      },
    });

    if (result.error) {
      resetIdempotencyKey(idempotencyKeyRef);
      return "failed";
    }

    refresh();
    return "success";
  } catch {
    // A thrown Stripe confirmation error is treated as a generic failure. Unlike 4xx
    // purchase-intent responses, it should not rotate the idempotency key.
    return "failed";
  }
}

export type ExpressCheckoutPurchaseArgs = {
  stripe: Stripe;
  elements: StripeElements;
};
type ExpressCheckoutPurchaseExecutorArgs = ExpressCheckoutPurchaseArgs & {
  pricingItem: PricingModelItem;
};

export async function executeExpressCheckoutPurchase(
  args: ExpressCheckoutPurchaseExecutorArgs,
  idempotencyKeyRef: IdempotencyKeyRef,
  refresh: RefreshPurchaseData
): Promise<PurchaseResult> {
  let response;

  try {
    response = await postPricingModelItemPurchaseIntent(args.pricingItem.id, {
      idempotency_key: getIdempotencyKey(idempotencyKeyRef),
      save_pm: true,
    });
  } catch (error) {
    logError(error);

    if (error instanceof ApiRequestError) {
      if (error.response?.status === 400) {
        // Express checkout matches the card-entry UX here: keep the failure modal
        // mounted, then let the caller refresh after dismiss to reconcile final state.
        resetIdempotencyKey(idempotencyKeyRef);
        return "failed_reload_on_close";
      }

      if (error.response?.status === 402) {
        // This attempt failed definitively, so future retries need a new idempotency key.
        resetIdempotencyKey(idempotencyKeyRef);
      }
    }

    return "failed";
  }

  if (response.status !== "pending" && response.status !== "processing") {
    return "failed";
  }

  const result = await args.stripe.confirmPayment({
    elements: args.elements,
    clientSecret: response.client_secret,
    confirmParams: {
      // Return to the same location after payment confirmation. It should load payment information & continue any existing flow that depends on payment.
      return_url: window.location.href,
    },
    redirect: "if_required",
  });

  if (result.error) {
    return "failed";
  }

  refresh();
  return "success";
}

export async function executeSubscriptionRetry(
  _args: void,
  idempotencyKeyRef: IdempotencyKeyRef,
  refresh: RefreshPurchaseData
) {
  try {
    const purchaseResponse = await purchaseSubscription({
      idempotency_key: getIdempotencyKey(idempotencyKeyRef),
    });

    if (purchaseResponse.status === "processing") {
      const stripeClient = await getStripeClient();
      const stripeResult = await stripeClient.confirmCardPayment(
        purchaseResponse.client_secret
      );

      if (stripeResult.error) {
        // Here it is an unrecoverable error that requires a new idempotency key.
        resetIdempotencyKey(idempotencyKeyRef);
        throw new Error(stripeResult.error.message);
      }
    }

    refresh();
  } catch (error) {
    logError(error);

    if (error instanceof ApiRequestError) {
      if (error.response?.status === 400) {
        // This idempotency key was already used and failed. Reload immediately because
        // the previous attempt may still have completed successfully.
        resetIdempotencyKey(idempotencyKeyRef);
        refresh();
        return;
      }

      if (error.response?.status === 402) {
        // This attempt failed definitively, so future retries need a new idempotency key.
        resetIdempotencyKey(idempotencyKeyRef);
      }
    }

    throw error;
  }
}

type PurchaseSubscriptionPlanPayload = {
  idempotency_key: string;
};
type CreatePurchaseIntentResponse = {
  client_secret: string;
  status: string;
};
export function purchaseSubscription(payload: PurchaseSubscriptionPlanPayload) {
  return api.post<PurchaseSubscriptionPlanPayload, CreatePurchaseIntentResponse>(
    "/subscription/plan/purchase",
    payload
  );
}

function getIdempotencyKey(idempotencyKeyRef: IdempotencyKeyRef) {
  idempotencyKeyRef.current = idempotencyKeyRef.current ?? generateIdempotencyKey();
  return idempotencyKeyRef.current;
}

function resetIdempotencyKey(idempotencyKeyRef: IdempotencyKeyRef) {
  idempotencyKeyRef.current = generateIdempotencyKey();
}

export function usePaymentRetryMutation<TArgs, TReturn>(
  paymentStatus: PaymentStatus | null | undefined,
  mutationFn: (
    args: TArgs,
    idempotencyKeyRef: IdempotencyKeyRef,
    refresh: RefreshPurchaseData
  ) => Promise<TReturn>
) {
  const idempotencyKeyRef = useRef<string>();
  const refresh = useRefreshPurchaseData();

  return useMutation({
    mutationFn: useSingleFlight(async (args: TArgs) => {
      trackEvent("subscription_payment_retry_clicked", {
        paymentStatus: paymentStatus?.status,
      });
      try {
        const result = await mutationFn(args, idempotencyKeyRef, refresh);
        trackEvent("subscription_payment_retry_succeeded");
        return result;
      } catch (error) {
        trackEvent("subscription_payment_retry_failed");
        throw error;
      }
    }),
  });
}

export function usePricingItemPurchaseMutation<TArgs extends object | void, TReturn>(
  // Absent until a plan is on screen. The hook has to be called either way, but
  // the mutation behind it cannot be triggered before one is chosen.
  pricingItem: PricingModelItem | undefined,
  mutationFn: (
    args: TArgs & { pricingItem: PricingModelItem },
    idempotencyKeyRef: IdempotencyKeyRef,
    refresh: RefreshPurchaseData
  ) => Promise<TReturn>
) {
  const idempotencyKeyRef = useRef<string>();
  const refresh = useRefreshPurchaseData();

  return useMutation({
    mutationFn: useSingleFlight(async (args: TArgs) => {
      if (!pricingItem) {
        throw new Error("A purchase was started before a plan was selected.");
      }
      const purchaseResult = await mutationFn(
        { ...args, pricingItem },
        idempotencyKeyRef,
        refresh
      );
      if (purchaseResult === "success") {
        trackPricingItemPurchase(pricingItem);
      } else {
        trackPricingItemPurchaseError(pricingItem);
      }
      return purchaseResult;
    }),
  });
}

export function useRefreshPurchaseData() {
  const queryClient = useQueryClient();
  const userContext = useRequiredContext(UserContext);

  return function refreshPurchaseData() {
    void userContext.refreshCurrentUser();
    void queryClient.refetchQueries(paymentMethodsQuery());
    void queryClient.refetchQueries(currentSubscriptionPlanQuery());
    void queryClient.refetchQueries({
      queryKey: availablePricingModelsQuery.baseKey,
    });
  };
}

// Return a number of days (including grace period) user has access to pro content.
export function getDaysWithinGracePeriod(subscription: SubscriptionPlan) {
  const dueDate = subscription.next_payment?.due_date;
  const gracePeriod =
    (subscription.next_payment?.grace_period &&
      Math.trunc(subscription.next_payment.grace_period / 24)) ||
    0;
  const userTZ = getUserTimezoneId();

  const tzAdjustedDueDate = moment.tz(dueDate, userTZ);
  return tzAdjustedDueDate.diff(moment.tz(userTZ), "days") + gracePeriod;
}
